import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { CourseCard } from "@jambushrushti/components/learning/CourseCard";
import { EnrollmentDialog } from "@jambushrushti/components/learning/EnrollmentDialog";
import {
  useCourses,
  useEnrollments,
  LMSCourse,
} from "@jambushrushti/hooks/useLMS";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import { Input } from "@jambushrushti/components/ui/input";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import {
  Search,
  Filter,
  BookOpen,
  GraduationCap,
  Video,
  Users,
  Sparkles,
  LogIn,
} from "lucide-react";
import { Skeleton } from "@jambushrushti/components/ui/skeleton";
import { Badge } from "@jambushrushti/components/ui/badge";

const SUBJECTS = [
  "All Subjects",
  "Agam",
  "Jain Philosophy",
  "Manuscripts",
  "Guruvani",
  "Jain History",
  "Languages",
];

const LEVELS = ["All Levels", "Beginner", "Intermediate", "Advanced"];
const MODES = ["All Modes", "Online", "Offline", "Hybrid"];

export default function Courses() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAdminAuth();
  const { data: courses, isLoading } = useCourses();
  const { data: enrollments } = useEnrollments();
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("All Subjects");
  const [level, setLevel] = useState("All Levels");
  const [mode, setMode] = useState("All Modes");

  // Enrollment dialog state
  const [selectedCourse, setSelectedCourse] = useState<LMSCourse | null>(null);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);

  // Get enrolled course IDs
  const enrolledCourseIds = new Set(enrollments?.map(e => e.course_id) || []);

  const handleEnrollClick = (course: LMSCourse) => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate("/learning/login", {
        state: { from: `/learning/courses/${course.id}` },
      });
      return;
    }
    setSelectedCourse(course);
    setEnrollDialogOpen(true);
  };

  const filteredCourses = courses?.filter(course => {
    const matchesSearch =
      course.title.toLowerCase().includes(search.toLowerCase()) ||
      course.description?.toLowerCase().includes(search.toLowerCase());
    const matchesSubject =
      subject === "All Subjects" || course.subject === subject;
    const matchesLevel = level === "All Levels" || course.level === level;
    const matchesMode = mode === "All Modes" || course.course_mode === mode;
    return matchesSearch && matchesSubject && matchesLevel && matchesMode;
  });

  // Calculate stats
  const totalCourses = courses?.length || 0;
  const onlineCourses =
    courses?.filter(c => c.course_mode === "Online").length || 0;
  const offlineCourses =
    courses?.filter(c => c.course_mode === "Offline").length || 0;

  return (
    <Layout>
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-3xl border border-primary/10 bg-gradient-to-br from-primary/10 via-primary/5 to-background p-8 md:p-10">
          {/* Decorative Elements */}
          <div className="absolute right-0 top-0 h-64 w-64 -translate-y-1/2 translate-x-1/2 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-48 w-48 -translate-x-1/2 translate-y-1/2 rounded-full bg-primary/5 blur-2xl" />

          <div className="relative z-10">
            <div className="mb-4 flex items-center gap-2">
              <Badge className="border-primary/20 bg-primary/10 font-medium text-primary">
                <Sparkles className="mr-1 h-3 w-3" />
                Learning Portal
              </Badge>
            </div>

            <h1 className="font-heading mb-3 text-3xl font-bold text-foreground md:text-4xl">
              Explore Our Courses
            </h1>
            <p className="mb-6 max-w-2xl text-lg text-muted-foreground">
              Discover a wide range of courses on Jain philosophy, scriptures,
              manuscripts, and more. Learn at your own pace with expert
              guidance.
            </p>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-3 rounded-xl border border-border bg-card/80 px-4 py-3 backdrop-blur-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <GraduationCap className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">
                    {totalCourses}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Courses</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-border bg-card/80 px-4 py-3 backdrop-blur-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10">
                  <Video className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">
                    {onlineCourses}
                  </p>
                  <p className="text-xs text-muted-foreground">Online</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-border bg-card/80 px-4 py-3 backdrop-blur-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">
                    {offlineCourses}
                  </p>
                  <p className="text-xs text-muted-foreground">Offline</p>
                </div>
              </div>
            </div>

            {/* Login CTA for non-authenticated users */}
            {!isAuthenticated && (
              <div className="mt-6 flex items-center gap-4">
                <Button asChild className="rounded-xl">
                  <Link to="/learning/login">
                    <LogIn className="mr-2 h-4 w-4" />
                    Login to Enroll
                  </Link>
                </Button>
                <span className="text-sm text-muted-foreground">
                  Sign in to enroll in courses and track your progress
                </span>
              </div>
            )}
            {isAuthenticated && (
              <div className="mt-6">
                <Button asChild variant="outline" className="rounded-xl">
                  <Link to="/learning/student-dashboard">
                    <GraduationCap className="mr-2 h-4 w-4" />
                    Go to My Dashboard
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="space-y-5 rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search courses by title, description..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-12 rounded-xl border-border bg-muted/50 pl-12 text-base focus:bg-background"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger className="h-11 w-full rounded-xl sm:w-[180px]">
                <SelectValue placeholder="Subject" />
              </SelectTrigger>
              <SelectContent className="border-border bg-card">
                {SUBJECTS.map(s => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="h-11 w-full rounded-xl sm:w-[150px]">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent className="border-border bg-card">
                {LEVELS.map(l => (
                  <SelectItem key={l} value={l}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="h-11 w-full rounded-xl sm:w-[150px]">
                <SelectValue placeholder="Mode" />
              </SelectTrigger>
              <SelectContent className="border-border bg-card">
                {MODES.map(m => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              className="h-11 rounded-xl border-border hover:bg-muted"
              onClick={() => {
                setSearch("");
                setSubject("All Subjects");
                setLevel("All Levels");
                setMode("All Modes");
              }}
            >
              <Filter className="mr-2 h-4 w-4" />
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Courses Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="space-y-4 rounded-2xl border border-border bg-card p-4"
              >
                <Skeleton className="aspect-[16/10] rounded-xl" />
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-11 w-full rounded-xl" />
              </div>
            ))}
          </div>
        ) : filteredCourses && filteredCourses.length > 0 ? (
          <>
            <div className="flex items-center justify-between">
              <p className="font-medium text-muted-foreground">
                Showing{" "}
                <span className="font-semibold text-foreground">
                  {filteredCourses.length}
                </span>{" "}
                course{filteredCourses.length !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredCourses.map(course => (
                <CourseCard
                  key={course.id}
                  course={course}
                  onEnroll={handleEnrollClick}
                  isEnrolled={enrolledCourseIds.has(course.id)}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-border bg-card py-20 text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-muted">
              <BookOpen className="h-10 w-10 text-muted-foreground/40" />
            </div>
            <h3 className="font-heading mb-3 text-2xl font-bold text-foreground">
              No courses found
            </h3>
            <p className="mx-auto max-w-md text-muted-foreground">
              Try adjusting your filters or check back later for new courses.
            </p>
            <Button
              variant="outline"
              className="mt-6 rounded-xl"
              onClick={() => {
                setSearch("");
                setSubject("All Subjects");
                setLevel("All Levels");
                setMode("All Modes");
              }}
            >
              <Filter className="mr-2 h-4 w-4" />
              Clear All Filters
            </Button>
          </div>
        )}
      </div>

      {/* Enrollment Dialog */}
      <EnrollmentDialog
        course={selectedCourse}
        open={enrollDialogOpen}
        onOpenChange={setEnrollDialogOpen}
      />
    </Layout>
  );
}
